import { useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { CLICKOUT_LABELS, type Placement, type PlacementList, type SiteDetail } from './types.ts';
import { ConfirmButton } from './ui.tsx';

const MODULE_LABELS: Record<string, string> = {
  coupon_list: 'Coupon list',
  featured_coupons: 'Featured coupons',
  seo_text: 'Links in SEO text',
  merchant_list: 'Merchant list',
};

/** Where the public API answers; the embed loads from there too. */
const PUBLIC_API_ORIGIN = (import.meta.env['VITE_PUBLIC_API_ORIGIN'] as string | undefined) ?? 'http://localhost:3001';

interface Draft {
  key: string;
  name: string;
  moduleType: string;
  clickoutMode: string;
  limit: string;
  layout: string;
  merchantSlugs: string;
  categorySlugs: string;
}

const emptyDraft: Draft = { key: '', name: '', moduleType: 'coupon_list', clickoutMode: '', limit: '', layout: '', merchantSlugs: '', categorySlugs: '' };

const toDraft = (p: Placement): Draft => ({
  key: p.key,
  name: p.name,
  moduleType: p.moduleType,
  clickoutMode: p.clickoutMode ?? '',
  limit: p.config.limit ? String(p.config.limit) : '',
  layout: p.config.layout ?? '',
  merchantSlugs: (p.config.merchantSlugs ?? []).join(', '),
  categorySlugs: (p.config.categorySlugs ?? []).join(', '),
});

const list = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const toInput = (d: Draft) => ({
  key: d.key.trim(),
  name: d.name.trim(),
  moduleType: d.moduleType,
  clickoutMode: d.clickoutMode || null,
  config: {
    ...(d.limit ? { limit: Number(d.limit) } : {}),
    ...(d.layout ? { layout: d.layout } : {}),
    ...(list(d.merchantSlugs).length ? { merchantSlugs: list(d.merchantSlugs) } : {}),
    ...(list(d.categorySlugs).length ? { categorySlugs: list(d.categorySlugs) } : {}),
  },
});

export function PlacementsPanel({ site }: { site: SiteDetail }) {
  const toast = useToast();
  const changes = useChanges();
  const data = useOp<PlacementList>('admin.placements.list', { siteRef: site.ref }, changes);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const embedded = site.type === 'embed_only' || site.type === 'api_only';

  const startEdit = (placement: Placement | null) => {
    setEditing(placement ? placement.ref : 'new');
    setDraft(placement ? toDraft(placement) : emptyDraft);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const ok =
      editing === 'new'
        ? await runWrite(toast, 'admin.placements.create', { siteRef: site.ref, ...toInput(draft) }, 'Placement added')
        : await runWrite(toast, 'admin.placements.update', { ref: editing, ...toInput(draft) }, 'Placement saved');
    if (ok) setEditing(null);
  };

  if (data.error) return <ErrorBox error={data.error} onRetry={data.reload} />;
  if (!data.data) return <Loading />;
  const placements = data.data.placements;
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div>
      <p className="muted small">A placement is one spot that shows coupons (a list, a featured block, links in text). Its ref goes into every clickout, so clicks and revenue can be split by spot.</p>
      <table className="table compact" aria-label="Placements">
        <thead>
          <tr>
            <th scope="col">Key</th>
            <th scope="col">Name</th>
            <th scope="col">Module</th>
            <th scope="col">Clickout</th>
            <th scope="col">Configuration</th>
            <th scope="col">Ref</th>
            <th scope="col" className="actions">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {placements.map((p) => (
            <tr key={p.ref}>
              <td>
                <code>{p.key}</code>
              </td>
              <td>{p.name}</td>
              <td>{MODULE_LABELS[p.moduleType] ?? p.moduleType}</td>
              <td>{p.clickoutMode ? CLICKOUT_LABELS[p.clickoutMode] : <span className="muted">Site setting</span>}</td>
              <td className="small">
                {[p.config.limit ? `${p.config.limit} items` : null, p.config.layout, p.config.merchantSlugs?.length ? `${p.config.merchantSlugs.length} merchants` : null, p.config.categorySlugs?.length ? `${p.config.categorySlugs.length} categories` : null]
                  .filter(Boolean)
                  .join(' · ') || <span className="muted">defaults</span>}
              </td>
              <td>
                <code>{p.ref}</code>
              </td>
              <td className="actions">
                {site.canAdmin ? (
                  <>
                    <button type="button" className="btn btn-small" onClick={() => startEdit(p)}>
                      Edit
                    </button>
                    <ConfirmButton
                      label="Remove"
                      title={`Remove the placement “${p.name}”?`}
                      confirmLabel="Remove placement"
                      onConfirm={() => runWrite(toast, 'admin.placements.remove', { ref: p.ref }, 'Placement removed')}
                    >
                      <p>Pages and embeds that use <code>{p.ref}</code> fall back to no placement. Past clicks keep the ref.</p>
                    </ConfirmButton>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
          {placements.length === 0 ? (
            <tr>
              <td colSpan={7} className="muted">
                No placements yet.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {site.canAdmin && editing === null ? (
        <button type="button" className="btn" onClick={() => startEdit(null)}>
          Add placement
        </button>
      ) : null}
      {editing !== null ? (
        <form className="card placement-form" onSubmit={submit} aria-label={editing === 'new' ? 'New placement' : 'Edit placement'}>
          <div className="form-grid cols-4">
            <label>
              Key
              <input required value={draft.key} placeholder="home_featured" pattern="[a-z0-9]+(_[a-z0-9]+)*" onChange={(e) => set('key', e.target.value)} />
            </label>
            <label>
              Name
              <input required minLength={2} value={draft.name} onChange={(e) => set('name', e.target.value)} />
            </label>
            <label>
              Module
              <select value={draft.moduleType} onChange={(e) => set('moduleType', e.target.value)}>
                {data.data.moduleTypes.map((m) => (
                  <option key={m} value={m}>
                    {MODULE_LABELS[m] ?? m}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Clickout
              <select value={draft.clickoutMode} onChange={(e) => set('clickoutMode', e.target.value)}>
                <option value="">Site setting</option>
                {Object.entries(CLICKOUT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Items
              <input type="number" min={1} max={100} value={draft.limit} placeholder="all" onChange={(e) => set('limit', e.target.value)} />
            </label>
            <label>
              Layout
              <select value={draft.layout} onChange={(e) => set('layout', e.target.value)}>
                <option value="">Default</option>
                <option value="list">List</option>
                <option value="grid">Grid</option>
                <option value="compact">Compact</option>
              </select>
            </label>
            <label>
              Only merchants (slugs)
              <input value={draft.merchantSlugs} placeholder="asos, nike" onChange={(e) => set('merchantSlugs', e.target.value)} />
            </label>
            <label>
              Only categories (slugs)
              <input value={draft.categorySlugs} placeholder="fashion" onChange={(e) => set('categorySlugs', e.target.value)} />
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {editing === 'new' ? 'Add placement' : 'Save placement'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}
      {embedded ? <EmbedUsage site={site} placements={placements} /> : null}
    </div>
  );
}

function EmbedUsage({ site, placements }: { site: SiteDetail; placements: Placement[] }) {
  const ref = placements[0]?.ref ?? 'plc_…';
  const snippet =
    site.type === 'embed_only'
      ? `<script type="module" src="${PUBLIC_API_ORIGIN}/element/v1/jetscale-coupons.js"></script>\n<jetscale-coupons placement="${ref}" api-key="YOUR_KEY"></jetscale-coupons>`
      : `curl -H "x-api-key: YOUR_KEY" "${PUBLIC_API_ORIGIN}/api/v1/ping"`;
  return (
    <section className="card embed-usage" aria-label="How to use this site">
      <h3 className="section-title">{site.type === 'embed_only' ? 'Embed snippet' : 'API usage'}</h3>
      <pre>
        <code>{snippet}</code>
      </pre>
      <p className="muted small">
        Use a key from <Link to={`/api-keys?site=${site.ref}`}>API keys</Link> and add the page's origin to it.
        {site.type === 'embed_only' ? ' The <jetscale-coupons> element (apps/element) is not shipped yet; this is the planned tag.' : ' Coupon endpoints beyond ping, reveal and votes are not public yet.'}
      </p>
    </section>
  );
}
